/**
 * BreathingVisual — the luminous orb at the heart of every Quiet Hours
 * session.
 *
 * The orb expands during inhale, holds at the top, contracts during
 * exhale, holds at the bottom. The transitions are smooth, not bouncy;
 * the easing is a long-ease (`easeInOut`) so the breath feels organic.
 *
 * Construction:
 *  - SVG `<circle>` with a soft radial gradient fill (not a flat color).
 *  - SVG `<filter>` with `feGaussianBlur` + `feMerge` for the soft glow
 *    around the orb. This is the allowed kind of SVG — filter
 *    primitives, not depictive imagery.
 *  - A faint inner glow ring + a wider outer halo ring, both pulsing
 *    in sync with the breath.
 *  - A short, plain-language phase label below the orb ("Breathe in",
 *    "Hold", "Breathe out") — never jargon, never decorative type.
 *
 * Timing source: the orb runs on a `getElapsedSec` callback the parent
 * supplies. When the parent passes `() => audio.currentTime` from the
 * voice track, the orb stays locked to the voice's actual timeline —
 * pausing the audio pauses the breath; seeking the audio jumps the
 * breath. When the parent passes nothing, the orb runs on a free
 * `performance.now()` clock (unguided sessions).
 */
import { useEffect, useState } from 'react';

import type { BreathingPattern } from './categories';
import { breathCycleSeconds } from './categories';

import './BreathingVisual.css';

export type BreathPhase = 'inhale' | 'hold-top' | 'exhale' | 'hold-bottom';

interface BreathingVisualProps {
  pattern: BreathingPattern;
  /** Hex orb fill color (or CSS color string). */
  orbColor: string;
  /** Hex outer halo color (or CSS color string). */
  haloColor: string;
  /**
   * Optional label override — if the parent wants no label (e.g.,
   * deep into a long sleep-aid session), pass `showLabel={false}`.
   */
  showLabel?: boolean;
  /**
   * Optional time source — called every frame and treated as seconds-
   * elapsed. When provided, the orb's phase is `phaseAt(pattern, t)`,
   * which means the parent can drive sync from `audio.currentTime`.
   * When omitted, the orb runs on its own clock.
   */
  getElapsedSec?: () => number;
}

const PHASE_LABELS: Record<BreathPhase, string> = {
  inhale: 'Breathe in',
  'hold-top': 'Hold',
  exhale: 'Breathe out',
  'hold-bottom': 'Rest',
};

/**
 * Compute the current breath phase + a 0..1 progress within that phase.
 * Pure function of (pattern, elapsedSec) — easy to test, easy to drive
 * deterministically from a frame loop.
 */
export function phaseAt(
  pattern: BreathingPattern,
  elapsedSec: number,
): { phase: BreathPhase; progress: number } {
  const cycle = breathCycleSeconds(pattern);
  const t = ((elapsedSec % cycle) + cycle) % cycle;
  if (t < pattern.inhale) {
    return { phase: 'inhale', progress: t / pattern.inhale };
  }
  const tAfterInhale = t - pattern.inhale;
  if (tAfterInhale < pattern.holdTop) {
    return { phase: 'hold-top', progress: tAfterInhale / pattern.holdTop };
  }
  const tAfterHoldTop = tAfterInhale - pattern.holdTop;
  if (tAfterHoldTop < pattern.exhale) {
    return { phase: 'exhale', progress: tAfterHoldTop / pattern.exhale };
  }
  const tAfterExhale = tAfterHoldTop - pattern.exhale;
  return { phase: 'hold-bottom', progress: tAfterExhale / pattern.holdBottom };
}

/** Map (phase, progress) → orb scale in [SCALE_MIN, SCALE_MAX]. */
const SCALE_MIN = 0.55;
const SCALE_MAX = 1.0;

/**
 * Mix a `#rrggbb` color toward black (negative `t`) or white (positive
 * `t`). `t = 0` returns the color unchanged. The math is plain linear
 * mix in sRGB — close enough for the small tint range we use here,
 * and dependency-free.
 */
function shiftLightness(hex: string, t: number): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  const target = t >= 0 ? 255 : 0;
  const a = Math.min(1, Math.abs(t));
  const mix = (c: number): string =>
    Math.round(c * (1 - a) + target * a)
      .toString(16)
      .padStart(2, '0');
  return `#${mix(r)}${mix(g)}${mix(b)}`;
}

export function scaleAt(phase: BreathPhase, progress: number): number {
  // Smooth s-curve for inhale / exhale; flat at the holds.
  const smooth = (x: number): number =>
    x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2;
  switch (phase) {
    case 'inhale':
      return SCALE_MIN + (SCALE_MAX - SCALE_MIN) * smooth(progress);
    case 'hold-top':
      return SCALE_MAX;
    case 'exhale':
      return SCALE_MIN + (SCALE_MAX - SCALE_MIN) * (1 - smooth(progress));
    case 'hold-bottom':
      return SCALE_MIN;
  }
}

export function BreathingVisual({
  pattern,
  orbColor,
  haloColor,
  showLabel = true,
  getElapsedSec,
}: BreathingVisualProps) {
  const [now, setNow] = useState(0);

  useEffect(() => {
    const t0 = performance.now();
    let raf = 0;
    const tick = () => {
      const t = getElapsedSec
        ? getElapsedSec()
        : (performance.now() - t0) / 1000;
      setNow(t);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [getElapsedSec]);

  const { phase, progress } = phaseAt(pattern, now);
  const scale = scaleAt(phase, progress);
  const label = PHASE_LABELS[phase];

  // Breath fullness ∈ [0, 1] — derived directly from the orb scale, so
  // it climbs smoothly during inhale, sits at 1 across hold-top,
  // descends across exhale, and rests at 0 across hold-bottom. Every
  // visual that should breathe in lockstep with the orb is driven by
  // this single value.
  const fullness = (scale - SCALE_MIN) / (SCALE_MAX - SCALE_MIN);

  // Color shift across the cycle: at the bottom of the breath the orb
  // sits in a deeper, banked shade; at the top it's lifted toward
  // white. The shift is continuous so the orb has a different colour
  // identity at every moment of the cycle — and the holds are read by
  // colour position even though the size is static.
  const tintedOrb = shiftLightness(orbColor, -0.22 + 0.34 * fullness);
  const tintedHalo = shiftLightness(haloColor, -0.18 + 0.28 * fullness);
  const centerStopOpacity = 0.7 + 0.25 * fullness;

  // Use a unique filter id so multiple instances don't collide. We only
  // ever render one breathing visual at a time today, but defensive.
  const filterId = 'qh-orb-glow';

  return (
    <div className="qh-breath">
      <svg
        className="qh-breath__svg"
        viewBox="0 0 600 600"
        aria-hidden="true"
        role="img"
      >
        <defs>
          <filter id={filterId} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="14" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <radialGradient id="qh-orb-fill" cx="42%" cy="38%" r="68%">
            <stop offset="0%" stopColor="#FFFFFF" stopOpacity={centerStopOpacity} />
            <stop offset="30%" stopColor={tintedOrb} stopOpacity="0.98" />
            <stop offset="70%" stopColor={tintedOrb} stopOpacity="0.78" />
            <stop offset="100%" stopColor={tintedOrb} stopOpacity="0.08" />
          </radialGradient>
          <radialGradient id="qh-orb-halo" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={tintedHalo} stopOpacity="0.7" />
            <stop offset="50%" stopColor={tintedHalo} stopOpacity="0.22" />
            <stop offset="100%" stopColor={tintedHalo} stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Outer halo — slightly larger than the orb, breathes with it. */}
        <circle
          cx="300"
          cy="300"
          r="240"
          fill="url(#qh-orb-halo)"
          opacity="0.85"
          transform={`scale(${scale * 1.05}) translate(${(1 - scale * 1.05) * 300 / (scale * 1.05)}, ${(1 - scale * 1.05) * 300 / (scale * 1.05)})`}
          style={{
            transformOrigin: '300px 300px',
            transformBox: 'fill-box',
          }}
        />

        {/* Inner orb — gradient fill + glow filter. Per-frame `scale`
         * from the breath math drives the size directly; no CSS
         * transition, because layering one on top of the already-
         * smooth s-curve acts as a low-pass filter and makes the orb
         * visibly lag the phase. The halo (above) and orb (here) must
         * use the same drive so they stay in lockstep. */}
        <g
          style={{
            transformOrigin: '300px 300px',
            transform: `scale(${scale})`,
          }}
        >
          <circle
            cx="300"
            cy="300"
            r="170"
            fill="url(#qh-orb-fill)"
            filter={`url(#${filterId})`}
            opacity="0.92"
          />
          {/* Soft highlight — slightly off-center to suggest dimensional light. */}
          <circle
            cx="270"
            cy="265"
            r="40"
            fill={tintedOrb}
            opacity="0.45"
            filter={`url(#${filterId})`}
          />
        </g>

      </svg>

      {/* Always render the label so the flex column reserves its height +
       * gap. Toggling `visibility` keeps the orb anchored when a spoken
       * subtitle replaces the breath label. `key={phase}` re-mounts the
       * element at each phase boundary so the fade-in keyframe re-runs
       * and the new word lands cleanly on the transition. */}
      <p
        key={phase}
        className="qh-breath__label"
        data-phase={phase}
        aria-live={showLabel ? 'polite' : 'off'}
        aria-hidden={!showLabel}
        style={!showLabel ? { visibility: 'hidden' } : undefined}
      >
        {label}
      </p>
    </div>
  );
}
